import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { HoffmanConfirmDialogComponent } from 'src/app/dialogs/hoffman-confirm-dialog/hoffman-confirm-dialog.component';
import { NotificationMessageComponent } from 'src/app/dialogs/notification-message/notification-message.component';
import { GeneralMessage, TypeIcon } from 'src/app/model/device/message.model';
import {
  CallPlace,
  FavoriteConfig
} from 'src/app/model/http/myActivity.model';
import { CommonService } from 'src/app/services/common.service';
import { DialogService } from 'src/app/services/dialog.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { ObservableService } from 'src/app/services/observable.service';
import { v4 as uuidv4 } from 'uuid';
import {
  BooleanCons,
  LineProductCons,
  MessageType,
  NumberCons, StringConst
} from '../../constants/const';
import { Messages } from '../../constants/messages';
import { RequestPiecesDialogComponent } from '../../dialogs/request-pieces-dialog/request-pieces-dialog.component';
import { IMoreProducts } from '../../model/http/shoppingList';
import { UpdateProduct } from '../../model/local/shopping-cart';
import { ShoppingCartService } from '../../services/shopping-cart.service';

@Component({
  selector: 'app-shopping-cart-button',
  templateUrl: './shopping-cart-button.component.html',
  styleUrls: ['./shopping-cart-button.component.scss'],
})
export class ShoppingCartButtonComponent implements OnInit, OnDestroy {
  @Input() container = StringConst.empty;
  @Input() label = StringConst.empty;
  @Input() quantity = NumberCons.zero;
  @Input() isLine = StringConst.empty;
  @Input() itemCode = StringConst.empty;
  @Input() request = CallPlace.default;
  @Input() configCartButton = new FavoriteConfig();
  @Output() wasSuccess = new EventEmitter<boolean>();
  @Input() disable = false;
  @Input() showBlueButton = false;
  @Input() isPackage = false;
  @Input() needContainerValidation = false;
  @Input() hoffmanProduct = BooleanCons.false;
  @Input() hoffmanText = StringConst.empty;
  @Input() isFromDialog = BooleanCons.false;
  subscription = new Subscription();
  isPhoneView = false;
  currentPices = 0;
  componentId = uuidv4();
  constructor(
    private commonService: CommonService,
    private observableService: ObservableService,
    private localStorageService: LocalStorageService,
    private shoppingCartService: ShoppingCartService,
    private dialogService: DialogService
  ) {
    this.request = CallPlace.default;
  }

  ngOnInit(): void {
    this.resultFromDialog();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  invalidProduct(): boolean {
    return this.commonService.validateAndValue([
      this.isLine === LineProductCons.no,
      !this.commonService.validateValidString(this.container)
    ]);
  }

  onClick(): void {
    if (this.invalidProduct()) {
      this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
        textToShow: Messages.invalidContainer,
        typeIcon: TypeIcon.error,
        typeMessage: MessageType.myFavorites,
      });
      return;
    }
    this.quantity = this.commonService.calculateTernary(this.request === CallPlace.myFavourites,
      1, this.quantity);
    this.currentPices = this.quantity;
    if (this.commonService.validateOrValue(this.request === CallPlace.detailOrderListEdit,
      this.request === CallPlace.detailProducts)) {
      this.validProduct();
    } else {
      this.openRequestPiecesDialog();
    }
  }

  addHoffmanProduct(): void {
    if (this.invalidProduct()) {
      this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
        textToShow: Messages.invalidContainer,
        typeIcon: TypeIcon.error,
        typeMessage: MessageType.myFavorites,
      });
      return;
    }
    this.openHoffmanTextDialog();
  }

  openHoffmanTextDialog(): void {
    const config = new MatDialogConfig();
    config.data = this.hoffmanText;
    this.dialogService.displayDialog(HoffmanConfirmDialogComponent, config).afterClosed().subscribe((res) => {
      if (res) {
        this.currentPices = this.commonService.calculateTernary(this.isFromDialog, this.quantity, 1);
        this.validProduct();
      }
    });
  }

  openRequestPiecesDialog(): void {
    const dialogConfig = new MatDialogConfig();
    const numberPieces = this.quantity;
    dialogConfig.data = numberPieces;
    const dictCallPlace: { [key: number]: boolean } = {
      0: false,
      1: true,
    };
    const dialogRef = this.dialogService.displayDialog(RequestPiecesDialogComponent, {
      data: {
        numberPieces,
        isPackage: dictCallPlace[Number(this.isPackage)],
      },
    });
    dialogRef.afterClosed().subscribe((dataFromDialog) => {
      if (dataFromDialog > 0) {
        this.currentPices = dataFromDialog;
        this.validProduct();
      }
    });
  }

  validProduct(): void {
    if (this.isLine === LineProductCons.yes) {
      this.sendProductToShoppingCart(true);
      return;
    }
    this.checkIfThereAreMoreProductsInShoppingCart();
  }

  checkIfThereAreMoreProductsInShoppingCart(): void {
    const queryParams = `?cardCode=${this.localStorageService.getCardCodeGeneral()}&itemcode=${this.itemCode
      }&typecart=${this.commonService.getCardTypeForRolShoppingCard(
        this.localStorageService.getUserInRole()
      )}`;
    this.shoppingCartService.checkMoreProducts(queryParams).subscribe(
      (res) => {
        const result = res.response ?? [];
        this.showDialog(result);
      });
  }

  showDialog(result: IMoreProducts[]): void {
    if (result.length > 0) {
      const filterAttrbite = result[0].itemAttributes.filter(attribute => this.commonService.validateAndValue([
        attribute.container.includes(this.container),
        attribute.label !== this.label
      ]));
      if (filterAttrbite.length > 0) {
        this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
          isButtonCancel: true,
          textToShow: Messages.addNewProduct,
          typeIcon: TypeIcon.question,
          typeMessage: MessageType.sendToShoppingCart,
          itemCode: this.itemCode,
          request: this.request,
          componentId: this.componentId,
        });
        return;
      }
      this.sendProductToShoppingCart(true);
      return;
    }
    this.sendProductToShoppingCart(true);
  }

  sendProductToShoppingCart(needSumQuantity: boolean): void {
    const productToSave = this.getProductToSave(needSumQuantity);
    this.shoppingCartService.updateProduct(productToSave).subscribe(
      () => {
        const messageToShow: { [key: number]: string } = {
          0: Messages.productAddedToCart,
          1: Messages.packageAddedToCart,
        };
        this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
          typeIcon: TypeIcon.success,
          textToShow: `${messageToShow[Number(this.isPackage)]} ${this.itemCode
            }`,
          isButtonCancel: false,
          typeMessage: MessageType.none,
        });
        this.observableService.setTotalproducts();
        this.wasSuccess.emit(true);
        this.observableService.setDismissView();
      },
      (error) => { this.wasSuccess.emit(false); });
  }

  getProductToSave(needSumQuantity: boolean): UpdateProduct {
    const cartType = this.commonService.getCardTypeForRolShoppingCard(
      this.localStorageService.getUserInRole()
    );
    const productToSave = new UpdateProduct();
    productToSave.cartType = cartType;
    productToSave.container = this.commonService.calculateTernary(this.isLine === LineProductCons.yes,
      LineProductCons.noApply,
      this.container);
    productToSave.itemCode = this.itemCode;
    productToSave.label = this.commonService.calculateTernary(this.isLine === LineProductCons.yes,
      LineProductCons.noApply,
      this.label);
    productToSave.needSumQuantity = true;
    productToSave.needsPersitance = true;
    productToSave.quantity = this.currentPices;
    productToSave.userId = this.localStorageService.getCardCodeGeneral();
    productToSave.isLine = StringConst.empty;
    productToSave.isPackage = this.isPackage;
    productToSave.needContainerValidation = this.needContainerValidation;
    productToSave.priceListId = this.localStorageService.getPriceList();
    return productToSave;
  }

  resultFromDialog(): void {
    this.subscription.add(
      this.observableService.getResultGeneralNotification().subscribe((res) => {
        if (res.componentId === this.componentId) {
          this.validateSendToCart(res);
          return;
        }
      })
    );
  }
  validateSendToCart(res: GeneralMessage): void {
    if (res.wasOk) {
      this.sendProductToShoppingCart(false);
    }
  }
}
